import React, { useState } from "react";
import axios from "axios";
import {
  Box,
  Flex,
  Button,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Text,
  useColorModeValue,
  useToast,
  HStack,
  Icon,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  IconButton,
  Image,
  Divider,
} from "@chakra-ui/react";
import { useHistory, Link } from "react-router-dom";
import config from "config";
import {
  FaEnvelope,
  FaLock,
  FaEye,
  FaEyeSlash,
  FaArrowRight,
  FaGlobe,
} from "react-icons/fa";
import { FiLogIn, FiLock } from "react-icons/fi";

function SignIn() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loginFailed, setLoginFailed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const history = useHistory();
  const toast = useToast();

  const titleColor = useColorModeValue("brand.600", "teal.300");
  const textColor = useColorModeValue("slate.600", "slate.300");
  const cardBg = useColorModeValue("white", "navy.700");
  const cardBorder = useColorModeValue("slate.200", "whiteAlpha.100");
  const inputBg = useColorModeValue("slate.50", "navy.800");
  const logoSrc = useColorModeValue("/xplogo2.png", "/xpwhitelogo.png");
  const errorColor = "red.400";

  const validateEmail = (val) => {
    const re = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return re.test(String(val).toLowerCase());
  };

  const validateForm = () => {
    return (
      email !== "" &&
      password !== "" &&
      validateEmail(email) &&
      email.length <= 100 &&
      password.length <= 30
    );
  };

  const showNotification = (title, description, duration, status) => {
    toast({
      title: title,
      description: description,
      status: status,
      duration: duration,
      isClosable: true,
    });
  };

  const handleSignIn = async (e) => {
    if (e) e.preventDefault();
    if (!validateForm()) {
      showNotification("Error", "Please provide a valid email and password", 5000, "error");
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await axios.post(`${config.API_URL}/auth/login`, {
        email,
        password,
      });
      const token = response.data.token;
      localStorage.setItem("token", token);
      localStorage.setItem("user_email", email.toLowerCase());

      let userRole = "USER";
      try {
        const payload = JSON.parse(atob(token.split(".")[1]));
        if (payload.role) {
          userRole = String(payload.role).toUpperCase();
        }
      } catch (err) {
        console.error("Error parsing token claims:", err);
      }

      showNotification(
        "Sign In Successful",
        `Logged in as ${email} (${userRole})`,
        4000,
        "success"
      );
      history.push("/txd");
    } catch (error) {
      console.error("Error signing in:", error);
      setLoginFailed(true);
      showNotification("Login failed", "Invalid email or password", 5000, "error");
      setPassword("");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Flex
      position="relative"
      minH="100vh"
      align="center"
      justify="center"
      p={{ base: "16px", md: "24px" }}
      pt={{ base: "70px", md: "80px" }}
      pb="40px"
    >
      <Box
        w="100%"
        maxW="460px"
        mx="auto"
        bg={cardBg}
        borderRadius="24px"
        border="1px solid"
        borderColor={cardBorder}
        shadow="xl"
        p={{ base: "28px", sm: "36px", md: "40px" }}
      >
        {/* Header */}
        <Box textAlign="center" mb={6}>
          <Flex justify="center" mb={3}>
            <Image
              src={logoSrc}
              alt="Tunisian XP Database"
              maxH="56px"
              objectFit="contain"
            />
          </Flex>
          <Heading
            color={titleColor}
            fontSize={{ base: "22px", md: "26px" }}
            fontWeight="800"
            letterSpacing="-0.02em"
            mb="6px"
          >
            Member Access
          </Heading>
          <Text color={textColor} fontSize="sm">
            Authorized portal for registered members & administrators
          </Text>
        </Box>

        <Box as="form" onSubmit={handleSignIn}>
          {/* Email Input */}
          <FormControl mb="18px">
            <FormLabel fontSize="xs" fontWeight="700" textTransform="uppercase" color={textColor} mb={1}>
              Email Address
            </FormLabel>
            <InputGroup size="lg">
              <InputLeftElement pointerEvents="none">
                <Icon as={FaEnvelope} color="teal.400" />
              </InputLeftElement>
              <Input
                borderRadius="14px"
                fontSize="sm"
                type="email"
                placeholder="name@institution.tn"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                isInvalid={!validateEmail(email) && email !== ""}
                bg={inputBg}
                borderColor={cardBorder}
                _focus={{
                  borderColor: "teal.400",
                  boxShadow: "0 0 15px rgba(13, 148, 136, 0.25)",
                }}
              />
            </InputGroup>
            {email !== "" && !validateEmail(email) && (
              <Text color={errorColor} fontSize="2xs" mt="4px">
                Please enter a valid email address.
              </Text>
            )}
          </FormControl>

          {/* Password Input */}
          <FormControl mb="22px">
            <Flex justify="space-between" align="center" mb={1}>
              <FormLabel fontSize="xs" fontWeight="700" textTransform="uppercase" color={textColor} mb={0}>
                Password
              </FormLabel>
              <Link
                to="/auth/request-password-reset"
                style={{ fontSize: "12px", fontWeight: "600", color: "#0d9488" }}
              >
                Forgot Password?
              </Link>
            </Flex>
            <InputGroup size="lg">
              <InputLeftElement pointerEvents="none">
                <Icon as={FaLock} color="teal.400" />
              </InputLeftElement>
              <Input
                borderRadius="14px"
                fontSize="sm"
                type={showPassword ? "text" : "password"}
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                isInvalid={password === "" && loginFailed}
                bg={inputBg}
                borderColor={cardBorder}
                _focus={{
                  borderColor: "teal.400",
                  boxShadow: "0 0 15px rgba(13, 148, 136, 0.25)",
                }}
              />
              <InputRightElement>
                <IconButton
                  size="sm"
                  variant="ghost"
                  colorScheme="teal"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  icon={showPassword ? <FaEyeSlash /> : <FaEye />}
                  onClick={() => setShowPassword(!showPassword)}
                />
              </InputRightElement>
            </InputGroup>
          </FormControl>

          {/* Submit Button */}
          <Button
            fontSize="sm"
            fontWeight="700"
            type="submit"
            colorScheme="teal"
            bgGradient="linear(to-r, teal.500, cyan.500)"
            color="white"
            w="100%"
            h="48px"
            borderRadius="14px"
            isLoading={isSubmitting}
            isDisabled={!validateForm() || isSubmitting}
            leftIcon={<FiLock />}
            shadow="0 6px 20px rgba(13, 148, 136, 0.35)"
            _hover={{
              bgGradient: "linear(to-r, teal.600, cyan.600)",
              shadow: "0 8px 25px rgba(13, 148, 136, 0.45)",
            }}
          >
            Member Sign In
          </Button>
        </Box>

        <Divider my={5} borderColor={cardBorder} />

        {/* Public Data Callout Box */}
        <Box
          p={4}
          borderRadius="16px"
          bg={useColorModeValue("teal.50", "whiteAlpha.50")}
          border="1px dashed"
          borderColor={useColorModeValue("teal.200", "teal.700")}
          textAlign="center"
        >
          <HStack justify="center" spacing={2} mb={1}>
            <Icon as={FaGlobe} color="teal.500" boxSize="15px" />
            <Text fontSize="xs" fontWeight="800" color={titleColor}>
              Looking for public data?
            </Text>
          </HStack>
          <Text fontSize="xs" color={textColor} mb={3}>
            Interactive maps, statistics and more data are freely accessible without an account.
          </Text>
          <Button
            size="sm"
            colorScheme="teal"
            variant="outline"
            borderRadius="full"
            px={5}
            onClick={() => history.push("/txd/mapdata")}
            rightIcon={<FaArrowRight size="11" />}
            _hover={{ bg: useColorModeValue("teal.100", "whiteAlpha.100") }}
          >
            Explore Public Dashboard
          </Button>
        </Box>
      </Box>
    </Flex>
  );
}

export default SignIn;

